import { DOCUMENT } from '@angular/common';
import * as i5 from '@angular/common/http';
import * as i0 from '@angular/core';
import { Inject, Injectable, NgModule } from '@angular/core';
import * as i6 from '@angular/router';
import * as i1 from '@upr-web-primer/app-utils';
import { paramBool, param, sendEvent, AutoSubscription, AppEnvironmentService, restoreScrollPosition } from '@upr-web-primer/app-utils';
import * as i2 from '@upr-web-primer/gds-common-utility';
import { GdsCommonUtilityService } from '@upr-web-primer/gds-common-utility';
import * as i3 from '@upr-web-primer/locale';
import { LocaleService } from '@upr-web-primer/locale';
import * as i4 from '@upr-web-primer/page-labels';
import { PageLabelsResolver } from '@upr-web-primer/page-labels';
import * as i7 from '@upr-web-primer/spinner';
import { SPINNER_KEYS } from '@upr-web-primer/spinner';
import * as i8 from '@upr-web-primer/upr-mobile-service';
import { WINDOW } from '@upr-web-primer/window';
import { pick, isString, get, keyBy, omit, set, isEmpty, unset, map as map$1 } from 'lodash-es';
import { DateTime, Info, Settings } from 'luxon';
import { fromEvent, firstValueFrom, map, shareReplay, from, catchError, of, zip } from 'rxjs';
import { __decorate, __param, __metadata } from 'tslib';
import * as i4$1 from '@upr-web-primer/dal';
import * as i7$1 from '@upr-web-primer/notification';
import * as i6$1 from '@upr-web-primer/gds-poi';
import * as i5$1 from '@upr-web-primer/storage';
import * as i7$2 from '@upr-web-primer/wcs-shop';
const CALENDAR_TRIGGER_EVENT_KEYS = {
  GET_MONTH: 'upr.calendar.getMonth',
  GET_MONTH_BLOCKOUT_DATE: 'udx.calendar.getMonthBlockoutDate',
  GET_MONTH_DINING: 'upr.calendar.getMonthDining',
  GET_MONTH_PARK_HOURS: 'upr.calendar.getMonthParkHours',
  GET_MONTH_SHOW_TIMES: 'upr.calendar.getMonthShowTimes',
  GET_MONTH_STORE: 'upr.calendar.getMonthStore',
  GET_MONTH_EVENTS: 'upr.calendar.getMonthEvents'
};
const CALENDAR_OUTPUT_EVENT_KEYS = {
  OUT_GET_MONTH: 'upr.calendar.getMonth.data',
  OUT_GET_MONTH_BLOCKOUT_DATE: 'udx.calendar.getMonthBlockoutDate.data',
  OUT_GET_MONTH_PARK_HOURS: 'upr.calendar.getMonthParkHours.data',
  OUT_GET_MONTH_DINING: 'upr.calendar.getMonthDining.data',
  OUT_GET_MONTH_SHOW_TIMES: 'upr.calendar.getMonthShowTimes.data',
  OUT_GET_MONTH_STORE: 'upr.calendar.getMonthStore.data',
  OUT_GET_MONTH_EVENTS: 'upr.calendar.getMonthEvents.data'
};
const CALENDAR_OUTPUT_EVENTS_WITH_ERROR_HANDLING = [CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_BLOCKOUT_DATE];
const GDS_CALENDAR_REPLACED_DEFAULTS = {
  gds_blockout_date_calendar: {
    use_google_calendar: paramBool('$gds_blockout_date_calendar.use_google_calendar$')
  },
  google_calendar: {
    api_key: param('$google_calendar.api_key$') || param('$google.calendar.apiKey$')
  }
};
const GDS_CALENDAR_ENV_DEFAULTS = {
  gds_blockout_date_calendar: {
    use_google_calendar: false
  },
  google_calendar: {
    api_key: ''
  }
};
let GdsCalendarBase = class GdsCalendarBase {
  constructor(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService) {
    this.window = window;
    this.appDefaults = appDefaults;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
    this.appEnvSrv.addDefaultValues(GDS_CALENDAR_ENV_DEFAULTS, GDS_CALENDAR_REPLACED_DEFAULTS);
    this.timezone = this.appDefaults.UPR_TIME_ZONE || DateTime.now().zoneName;
    this.locale = this.getLocale();
    this.longMonths = Info.months('long', {
      locale: this.locale
    });
    this.shortMonths = Info.months('short', {
      locale: this.locale
    });
    this.longWeekdays = Info.weekdays('long', {
      locale: this.locale
    });
    this.longWeekdays = this.unshiftPop(this.longWeekdays);
    this.shortWeekdays = Info.weekdays('short', {
      locale: this.locale
    });
    this.shortWeekdays = this.unshiftPop(this.shortWeekdays);
    this.numberOfMonths = Info.months('numeric');
    this.pageLabelsService.get().then(labels => {
      this.pageLabels = labels;
      this.nextLabel = this.pageLabels['upr.calendar.next_label'] || 'Next Month';
      this.prevLabel = this.pageLabels['upr.calendar.prev_label'] || 'Previous Month';
    });
  }
  unshiftPop(this_array) {
    const new_array = [];
    new_array[0] = this_array[this_array.length - 1];
    for (let i = 1; i < this_array.length; i++) {
      new_array[i] = this_array[i - 1];
    }
    return new_array;
  }
  combineCalendarData(currentData, previousData) {
    currentData.months.unshift(...previousData.months);
    currentData.monthsByName = {
      ...previousData.monthsByName,
      ...currentData.monthsByName
    };
    currentData.monthsByNumber = {
      ...previousData.monthsByNumber,
      ...currentData.monthsByNumber
    };
    currentData.startDate = previousData.startDate;
    return currentData;
  }
  getDayPastPresentFuture(date, zone = this.timezone) {
    const currentTime = DateTime.local({
      zone
    });
    const obj = {
      isCurrent: false,
      isPast: false,
      isFuture: false,
      isLast: false
    };
    obj.isPast = date.toISODate() < currentTime.toISODate();
    obj.isCurrent = date.toISODate() === currentTime.toISODate();
    obj.isFuture = date.toISODate() > currentTime.toISODate();
    return obj;
  }
  getMonthPastPresentFuture(date, startDate, endDate, zone = this.timezone, setCurrentToStartDate = false) {
    const currentTime = setCurrentToStartDate ? startDate : DateTime.local({
      zone
    });
    const obj = {
      isCurrent: false,
      isPast: false,
      isFuture: false,
      isLast: false
    };
    obj.isPast = this.isSameMonth(date, currentTime) ? false : date < currentTime;
    obj.isCurrent = this.isSameMonth(date, currentTime);
    obj.isFuture = this.isSameMonth(date, currentTime) ? false : date > currentTime;
    obj.isLast = endDate.month === date.month && endDate.year === date.year;
    return obj;
  }
  isSameMonth(date, currentTime) {
    return date.month === currentTime.month && date.year === currentTime.year;
  }
  isSameDay(date, currentTime) {
    return date.day === currentTime.day && date.month === currentTime.month && date.year === currentTime.year;
  }
  getCalendarDate(date, zone = this.timezone) {
    const obj = pick(date.toObject(), ['year', 'day', 'month']);
    const checkTimes = this.getDayPastPresentFuture(date, zone);
    return {
      isoDate: date.toISO(),
      date: obj,
      ...checkTimes
    };
  }
  checkDates({
    startDate = DateTime.now().setZone(this.timezone),
    endDate,
    zone,
    monthCount
  }) {
    endDate = endDate || startDate;
    const luxStartTime = (isString(startDate) ? DateTime.fromISO(startDate) : startDate).setZone(zone).setLocale(this.locale);
    let luxEndTime = (isString(endDate) ? DateTime.fromISO(endDate) : endDate).setZone(zone).setLocale(this.locale);
    if (monthCount) {
      const luxRequestTime = (isString(startDate) ? DateTime.fromISO(startDate) : startDate).plus({
        months: monthCount - 1
      }).endOf('month');
      if (luxRequestTime.toISODate() < luxEndTime.toISODate()) {
        luxEndTime = luxRequestTime;
      }
    }
    if (!luxStartTime.isValid || !luxEndTime.isValid) {
      throw new Error(`Month ${this.errorMsg}`);
    }
    // EndTime cannot be before start date.
    if (luxEndTime.toISODate() < luxStartTime.toISODate()) {
      throw new Error('End date time cannot be before start date time.');
    }
    return {
      luxStartTime,
      luxEndTime
    };
  }
  getLocale() {
    const locale = this.localeService.getFromUrl();
    return `${locale.language}-${locale.country}`;
  }
  reduceCalItemToNumber(arr) {
    return arr.reduce((prev, cur) => {
      let key = cur.number;
      if (cur.year) {
        key = `${cur.year}-${key}`;
      }
      prev[key] = cur;
      return prev;
    }, {});
  }
  reduceCalItemToName(arr) {
    return arr.reduce((prev, cur) => {
      let key = cur.name;
      if (cur.year) {
        key = `${cur.year}-${key}`;
      }
      prev[key] = cur;
      return prev;
    }, {});
  }
  turnDaysToWeeks(arr) {
    const weeks = [];
    let week = [];
    const daysLength = arr.length;
    arr.forEach((val, index) => {
      week.push(val);
      if (val.weekday === 6 || index === daysLength - 1) {
        if (week.length < 7) {
          const placeHolder = Array(7 - week.length).fill(null);
          val.weekday === 6 ? week.unshift(...placeHolder) : week.push(...placeHolder);
        }
        weeks.push(week);
        week = [];
      }
    });
    return weeks;
  }
  registerEvents(events) {
    events.forEach(event => {
      this.subscriptions.add(fromEvent(this.window, event.name).subscribe(async data => {
        try {
          const detail = data.detail;
          const triggeredData = await event.callback.call(this, detail);
          sendEvent(event.outputName, event.outputCallback.call(this, triggeredData));
        } catch (err) {
          console.error('Failure to get calendar data', err);
          const hasErrorHandling = CALENDAR_OUTPUT_EVENTS_WITH_ERROR_HANDLING.includes(event.outputName);
          if (hasErrorHandling) {
            sendEvent(event.outputName, err);
          }
        }
      }));
    });
  }
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngOnDestroy() {
    /* Needed for AutoSubscription */
  }
};
GdsCalendarBase = __decorate([AutoSubscription(), __param(0, Inject(WINDOW)), __param(1, Inject('AppDefaults')), __metadata("design:paramtypes", [Object, Object, AppEnvironmentService, GdsCommonUtilityService, LocaleService, PageLabelsResolver])], GdsCalendarBase);
class GdsCalendarService extends GdsCalendarBase {
  constructor(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService) {
    super(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.window = window;
    this.appDefaults = appDefaults;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
    this.init();
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH,
      callback: this.getMonth,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  getDay({
    time = DateTime.now().setZone(this.timezone),
    zone = this.timezone
  }) {
    const luxTime = (isString(time) ? DateTime.fromISO(time) : time).setZone(zone).setLocale(this.locale);
    if (!luxTime.isValid) {
      throw new Error(`Day ${this.errorMsg}`);
    }
    const currentLuxTime = DateTime.local({
      zone: this.timezone
    });
    const checkTimes = this.getDayPastPresentFuture(luxTime, zone);
    const dayObj = {
      name: luxTime.weekdayLong,
      nameShort: luxTime.weekdayShort,
      number: luxTime.day,
      date: luxTime.toISO(),
      shortDate: luxTime.toLocaleString(DateTime.DATE_MED),
      longDate: luxTime.toLocaleString(DateTime.DATE_HUGE),
      weekday: luxTime.weekday,
      isSameWeek: currentLuxTime.weekNumber === luxTime.weekNumber,
      isSameMonth: currentLuxTime.month === luxTime.month,
      isSameYear: currentLuxTime.year === luxTime.year,
      ...checkTimes
    };
    if (zone !== this.timezone) {
      dayObj.timezone = zone;
    }
    return dayObj;
  }
  async getMonth({
    startDate = DateTime.now().setZone(this.timezone),
    endDate,
    zone = this.timezone,
    item = {},
    previous,
    monthCount,
    setCurrentToStartDate = false
  }) {
    // Pulling label overrides from Tridion resources
    const resources = get(item, 'resources', null);
    if (resources) {
      const formattedResources = keyBy(get(item, 'resources.Resource', []), res => get(res, 'Key', '').toUpperCase());
      this.setResourceOverrides(formattedResources);
    }
    const {
      luxStartTime,
      luxEndTime
    } = this.checkDates({
      startDate,
      endDate,
      zone,
      monthCount
    });
    // We will need to determine month range for this below value, default to 1 if 0
    const range = Math.floor(luxEndTime.diff(luxStartTime, 'months').as('months')) + 1;
    // Fill an array and remake it with correct data by date
    const months = Array(range).fill(null).map((_, index) => {
      const newTime = luxStartTime.plus({
        month: index
      }).startOf('month');
      const daysInMonth = newTime.daysInMonth;
      const days = Array(daysInMonth).fill(null).map((_, index) => this.getDay({
        time: newTime.plus({
          day: index
        }),
        zone,
        item
      }));
      const daysByNumber = this.reduceCalItemToNumber(days);
      const daysByWeeks = this.turnDaysToWeeks(days);
      const checkTimes = this.getMonthPastPresentFuture(newTime, luxStartTime, luxEndTime, zone, setCurrentToStartDate);
      const longDate = this.monthHeading ? this.gdsCommonUtilities.replaceHashCodes(this.monthHeading, {
        month: this.longMonths[newTime.month - 1],
        year: newTime.year.toString()
      }) : newTime.toLocaleString({
        month: 'long',
        year: 'numeric'
      });
      const ariaLabel = this.monthAriaLabel ? this.gdsCommonUtilities.replaceHashCodes(this.monthAriaLabel, {
        monthheading: longDate
      }) : undefined;
      const monthObj = {
        ariaLabel,
        name: newTime.monthLong,
        nameShort: newTime.monthShort,
        number: newTime.month,
        year: newTime.year,
        shortDate: newTime.toLocaleString({
          month: 'short',
          year: 'numeric'
        }),
        longDate,
        daysInMonth,
        days,
        daysByNumber,
        daysByWeeks,
        ...checkTimes
      };
      return monthObj;
    });
    const items = {
      startDate: luxStartTime,
      endDate: luxEndTime,
      ...item
    };
    return {
      months,
      zone,
      previous,
      item: items
    };
  }
  getCalendarData({
    months,
    zone = this.timezone,
    item,
    previous
  }) {
    const monthsByName = this.reduceCalItemToName(months);
    const monthsByNumber = this.reduceCalItemToNumber(months);
    const startDate = this.getCalendarDate(item.startDate, zone);
    const endDate = this.getCalendarDate(item.endDate, zone);
    let dataObj = {
      info: {
        data: {
          ...omit(item, ['startDate', 'endDate'])
        },
        longMonths: this.longMonths,
        shortMonths: this.shortMonths,
        longWeekdays: this.longWeekdays,
        shortWeekdays: this.shortWeekdays,
        numberOfMonths: this.numberOfMonths,
        nextLabel: this.nextLabel,
        prevLabel: this.prevLabel,
        locale: this.locale,
        timezone: zone
      },
      months,
      monthsByName,
      monthsByNumber,
      startDate,
      endDate
    };
    if (previous) {
      dataObj = this.combineCalendarData(dataObj, previous);
    }
    return dataObj;
  }
  setResourceOverrides(resources) {
    this.monthAriaLabel = get(resources, 'CALENDARMONTHARIALABEL.Value', '');
    this.monthHeading = get(resources, 'CALENDARMONTHHEADING.Value', '');
    const monthVarNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    monthVarNames.forEach((name, index) => {
      const monthLabel = get(resources, `MONTH${name}LABEL.Value`, '');
      if (monthLabel) {
        this.longMonths[index] = monthLabel;
      }
    });
    const weekdayVarNames = ['SUN', 'MON', 'TUE', 'WED', 'THUR', 'FRI', 'SAT'];
    weekdayVarNames.forEach((name, index) => {
      let dayLabel = get(resources, `DAY${name}LABEL.Value`, '');
      let ariaLabel = get(resources, `DAY${name}ARIALABEL.Value`, '');
      // To support both spellings of Thursday abbreviation used in Tridion
      if (name === 'THUR' && !dayLabel) {
        dayLabel = get(resources, 'DAYTHRLABEL.Value', '');
      }
      if (name === 'THUR' && !ariaLabel) {
        ariaLabel = get(resources, 'DAYTHRARIALABEL.Value', '');
      }
      if (dayLabel) {
        this.shortWeekdays[index] = {
          label: dayLabel,
          ariaLabel: ariaLabel || this.longWeekdays[index]
        };
      }
    });
  }
  static {
    this.ɵfac = function GdsCalendarService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.PageLabelsResolver));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarService,
      factory: GdsCalendarService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4.PageLabelsResolver
  }], null);
})();
class GdsCalendarBlockoutDateService extends GdsCalendarService {
  constructor(document, window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService, http, route, router, spinner, uprMobileService) {
    super(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.document = document;
    this.window = window;
    this.appDefaults = appDefaults;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
    this.http = http;
    this.route = route;
    this.router = router;
    this.spinner = spinner;
    this.uprMobileService = uprMobileService;
    this.endpointDateFormat = 'MM/dd/yyyy';
    this.cache = {};
    this.gdsBlockoutDateConfig = this.appEnvSrv.getEnvVariables('gds_blockout_date_calendar');
    this.googleCalendarConfig = this.appEnvSrv.getEnvVariables('google_calendar');
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_BLOCKOUT_DATE,
      callback: this.getMonthBlockoutDate,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_BLOCKOUT_DATE,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  initComponent(component, request, dropdowns, errorDetail) {
    const defaultParkId = get(request, 'item.park.default', null);
    const defaultPassId = get(request, 'item.pass.default', null);
    const queryParams = this.route.snapshot.queryParams;
    const targetParkId = get(queryParams, 'parkType');
    const targetPassId = get(queryParams, 'passType');
    const defaultQueryParams = this.getDropdownQueryParams(dropdowns, {
      default: defaultParkId,
      target: targetParkId
    }, {
      default: defaultPassId,
      target: targetPassId
    });
    this.router.navigate([], {
      queryParams: defaultQueryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    set(request, 'item.park.target', defaultQueryParams.parkType || defaultParkId);
    set(request, 'item.pass.target', defaultQueryParams.passType || defaultPassId);
    this.updateComponentData(component, request, dropdowns, errorDetail);
    component.addEventListener('valueChange', e => {
      const type = get(e, 'target.data.type', '');
      const val = get(e, 'target.value', null);
      if (type === 'park-type') {
        const prevParkType = get(request, 'item.park.target');
        if (prevParkType === val) {
          return;
        }
        set(request, 'item.park.target', val);
      } else if (type === 'pass-type') {
        const prevPassType = get(request, 'item.pass.target');
        if (prevPassType === val) {
          return;
        }
        set(request, 'item.pass.target', val);
      }
      this.updateComponentData(component, request, dropdowns, errorDetail);
    });
  }
  getDay({
    time = DateTime.now().setZone(this.timezone),
    zone = this.timezone,
    item = {}
  }) {
    const day = super.getDay({
      time,
      zone
    });
    this.dayManipulation(day, {
      time,
      zone,
      item
    });
    return day;
  }
  async getMonthBlockoutDate({
    startDate,
    endDate,
    zone = this.timezone,
    item = {},
    previous,
    monthCount
  }) {
    if (!startDate) {
      startDate = DateTime.now().setZone(zone);
    }
    const data = await this.getMonthBlockoutDateData({
      startDate,
      endDate,
      zone,
      item,
      monthCount
    });
    const months = await this.getMonth({
      startDate: data?.firstAvailableDate?.toISO(),
      endDate: data?.lastAvailableDate?.endOf('month')?.toISO(),
      zone,
      item: data,
      previous,
      setCurrentToStartDate: true,
      monthCount
    });
    return months;
  }
  // Call this function before sending back
  getCalendarData({
    months,
    zone,
    item
  }) {
    const data = super.getCalendarData({
      months,
      zone,
      item
    });
    return data;
  }
  dayManipulation(day, {
    zone = this.timezone,
    item
  }) {
    const currDate = DateTime.fromISO(day.date).setZone(zone).setLocale(this.getLocale());
    const formattedDate = currDate.toFormat('yyyy-MM-dd');
    const blockoutDates = get(item, 'blockoutDates', null);
    const parkId = get(item, 'parkId', '');
    let statusCode;
    if (this.gdsBlockoutDateConfig.use_google_calendar) {
      const isBlockedDate = get(blockoutDates, formattedDate, day.isPast);
      statusCode = isBlockedDate ? '1' : '2';
    } else if (day.isPast) {
      statusCode = get(blockoutDates, ['statuses', parkId, 'disabled'], '');
    } else {
      statusCode = get(blockoutDates, ['dates', formattedDate, parkId, 'PassStatusCode'], '');
    }
    const legendData = get(item, ['filteredLegend', statusCode]);
    const style = get(legendData, 'calendarDay.style', '');
    const description = get(legendData, 'description', '');
    day.style = style;
    day.noninteractive = true;
    day.available = true;
    day.ariaLabel = `${currDate.toLocaleString(DateTime.DATE_FULL)} ${description}`;
  }
  async getMonthBlockoutDateData({
    endDate,
    zone = this.timezone,
    item = {},
    monthCount
  }) {
    const endDateObject = typeof endDate === 'string' ? DateTime.fromISO(endDate).setZone(zone) : endDate;
    const endDateFormatted = endDateObject?.toFormat(this.endpointDateFormat) ?? '';
    const passId = get(item, 'pass.target', '');
    let parkId = get(item, 'park.target', '');
    const calendarUrl = get(item, ['calendarUrls', passId], '');
    const keyItems = get(item, 'keyItems', []);
    const legend = keyItems.reduce((legend, item) => {
      item.key.split(',').forEach(key => {
        legend[key] = item;
      });
      return legend;
    }, {});
    const firstAvailableDate = DateTime.now().setZone(zone);
    let lastAvailableDate;
    let blockoutDates;
    let filteredLegend;
    if (this.gdsBlockoutDateConfig.use_google_calendar) {
      blockoutDates = await this.getGoogleCalendarData(calendarUrl);
      lastAvailableDate = endDateObject || firstAvailableDate.plus({
        months: monthCount
      });
      filteredLegend = legend;
    } else {
      blockoutDates = await this.getBlockoutDateServiceData(passId, endDateFormatted, legend);
      const allDates = Object.keys(blockoutDates.dates);
      const lastDateISO = allDates[allDates.length - 1];
      lastAvailableDate = allDates.length ? DateTime.fromISO(lastDateISO).setZone(zone) : DateTime.now().endOf('month').setZone(zone);
      const statusKeys = Object.keys(get(blockoutDates, 'statuses', {}));
      if (!parkId && statusKeys[0]) {
        parkId = statusKeys[0];
        set(item, 'park.target', parkId);
        set(item, 'park.default', parkId);
      }
      filteredLegend = Object.keys(legend).reduce((override, statusCode) => {
        const style = get(legend, [statusCode, 'calendarDay', 'style'], '');
        const statusOverride = get(blockoutDates, ['statuses', parkId, style], '');
        override[statusCode] = legend[statusOverride];
        return override;
      }, {});
    }
    return {
      blockoutDates,
      filteredLegend,
      keyItems,
      firstAvailableDate,
      lastAvailableDate,
      parkId,
      passId,
      resources: item.resources
    };
  }
  getBlockoutDateServiceData(passId, endDateFormatted, legend) {
    if (this.cache[passId]) {
      return firstValueFrom(this.cache[passId]);
    } else {
      const apiCall = this.uprMobileService.fetchBlockoutDatesById(passId, endDateFormatted).pipe(map(resp => resp.reduce((parsedDates, currDate) => {
        const isoDate = get(currDate, 'Date', '');
        const date = DateTime.fromISO(isoDate, {
          setZone: true
        }).toFormat('yyyy-MM-dd');
        const statusPerVenue = get(currDate, 'StatusPerVenue', []);
        parsedDates.dates[date] = keyBy(statusPerVenue, status => {
          const placeId = get(status, 'PlaceId', '');
          const passStatusCode = get(status, 'PassStatusCode', '');
          if (!parsedDates.statuses[placeId]) {
            parsedDates.statuses[placeId] = {};
          }
          const keyStyle = get(legend, [passStatusCode, 'calendarDay', 'style'], '');
          const currStyledStatusCode = parsedDates.statuses[placeId][keyStyle];
          if (keyStyle && (!currStyledStatusCode || currStyledStatusCode < passStatusCode)) {
            parsedDates.statuses[placeId][keyStyle] = passStatusCode;
          }
          return placeId;
        });
        return parsedDates;
      }, {
        dates: {},
        statuses: {}
      })), shareReplay());
      this.cache[passId] = apiCall;
      return firstValueFrom(apiCall);
    }
  }
  getGoogleCalendarData(url) {
    if (this.cache[url]) {
      return firstValueFrom(this.cache[url]);
    }
    const apiCall = this.http.get(`${url}&key=${this.googleCalendarConfig.api_key}`).pipe(map(data => {
      const items = get(data, 'items', []);
      return items.reduce((blockoutDates, item) => {
        const startDate = get(item, 'start.date', '');
        blockoutDates[startDate] = true;
        return blockoutDates;
      }, {});
    }), shareReplay());
    this.cache[url] = apiCall;
    return firstValueFrom(apiCall);
  }
  async updateComponentData(component, request, dropdowns, errorDetail) {
    const gdsArticleDetailErrorEl = component.parentElement?.querySelector('.gds-article-detail-blockout-error');
    const parsedData = this.gdsCommonUtilities.getComponentData(component);
    const timezoneData = get(request, 'item.timezone');
    try {
      this.spinner.show('gds-blockout-date-calendar');
      const blockoutData = await this.getMonthBlockoutDate(request);
      const calendarDetail = this.getCalendarData(blockoutData);
      const months = get(calendarDetail, 'months', []);
      if (isEmpty(months)) {
        throw 'No months found!';
      }
      const calendarItemData = get(request, 'item', {});
      const calendarInfo = get(calendarDetail, 'info', '');
      const calendarData = get(calendarInfo, 'data', null);
      const passData = get(calendarItemData, 'pass', null);
      const defaultPassId = get(passData, 'default', '');
      const passId = get(passData, 'target') || defaultPassId;
      // Filter out unused keys from legend
      const keyItems = get(calendarData, 'keyItems', []);
      const mappedLegend = get(calendarData, 'filteredLegend', {});
      const configuredStyles = {};
      const filteredKeyItems = keyItems.reduce((items, item) => {
        const style = get(item, 'calendarDay.style', '');
        const key = get(item, 'key', '').split(',')[0];
        const mappedKeyItem = mappedLegend[key];
        if (!mappedKeyItem && style !== 'disabled' || configuredStyles[style]) {
          return items;
        }
        configuredStyles[style] = true;
        items.push(mappedKeyItem ?? item);
        return items;
      }, []);
      // Filter out unavilable parks
      const parkData = get(calendarItemData, 'park', null);
      const defaultParkId = get(parkData, 'default', '');
      let parkId = get(parkData, 'target') || defaultParkId;
      let isSelectedParkAvailable = true;
      const statuses = get(calendarData, 'blockoutDates.statuses', {});
      const filteredDropdowns = dropdowns.map(dropdown => {
        const type = get(dropdown, 'data.type', '');
        if (type === 'park-type') {
          const processedParkData = this.processParkDropdown(dropdown, parkId, defaultParkId, statuses);
          parkId = processedParkData.parkId;
          isSelectedParkAvailable = processedParkData.isSelectedParkAvailable;
          return processedParkData.dropdown;
        } else {
          return {
            ...dropdown,
            defaultValue: passId
          };
        }
      });
      if (!isSelectedParkAvailable) {
        this.updateComponentData(component, {
          ...request,
          item: {
            ...request.item,
            park: {
              default: defaultParkId,
              target: parkId
            },
            pass: {
              default: defaultPassId,
              target: passId
            }
          }
        }, dropdowns);
        return;
      }
      if (gdsArticleDetailErrorEl) {
        gdsArticleDetailErrorEl.remove();
      }
      component.info = calendarInfo;
      component.requested = months;
      component.data = {
        timezone: timezoneData,
        dropdowns: filteredDropdowns,
        key: {
          ...parsedData.key,
          items: filteredKeyItems
        }
      };
      const newQueryParams = this.getDropdownQueryParams(filteredDropdowns, parkData, passData);
      restoreScrollPosition(() => {
        return this.router.navigate([], {
          queryParams: newQueryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      });
    } catch {
      component.requested = [];
      if (!component.data) {
        component.data = {
          timezone: timezoneData,
          dropdowns
        };
      }
      if (errorDetail && !gdsArticleDetailErrorEl) {
        const gdsArticleDetail = this.document.createElement('gds-article-detail');
        gdsArticleDetail.setAttribute('data', JSON.stringify(errorDetail));
        gdsArticleDetail.setAttribute('type', 'center');
        gdsArticleDetail.classList.add('gds-article-detail-blockout-error');
        gdsArticleDetail.setAttribute('theme', component.theme);
        component.parentElement?.appendChild(gdsArticleDetail);
      }
    } finally {
      this.spinner.hide('gds-blockout-date-calendar');
    }
  }
  /**
   * Method to check if a park is available based on statuses.
   *
   * @param targetParkId
   * @param statuses
   * @returns
   */
  checkAvailability(targetParkId, statuses) {
    if (!statuses[targetParkId]) return true;
    const statusKeys = Object.keys(statuses[targetParkId]);
    const hasAvailability = statusKeys.length !== 0 && (statusKeys.length > 1 || !statusKeys.includes('disabled'));
    return hasAvailability;
  }
  /**
   * Method to filter out unavailable parks from dropdown options
   *
   * @param dropdown
   * @param currParkId
   * @param statuses
   * @returns
   */
  processParkDropdown(dropdown, currParkId, defaultParkId, statuses) {
    let parkId = currParkId;
    let isSelectedParkAvailable = true;
    const dropdownData = get(dropdown, 'data', null);
    const options = get(dropdownData, 'options', []);
    const filteredOptions = options.filter(opt => {
      const targetParkId = opt.value;
      const hasAvailability = this.checkAvailability(targetParkId, statuses);
      if (!hasAvailability && targetParkId === currParkId) {
        parkId = defaultParkId;
        isSelectedParkAvailable = false;
      }
      return hasAvailability;
    });
    return {
      dropdown: {
        ...dropdown,
        data: {
          ...dropdownData,
          formField: {
            ...dropdownData?.formField
          },
          options: [...filteredOptions]
        },
        defaultValue: parkId
      },
      isSelectedParkAvailable,
      parkId
    };
  }
  getDropdownQueryParams(dropdowns, park, pass) {
    const defaultParkId = get(park, 'default', null);
    const defaultPassId = get(pass, 'default', null);
    let targetParkId = get(park, 'target', null);
    let targetPassId = get(pass, 'target', null);
    dropdowns.forEach(dropdown => {
      const type = get(dropdown, 'data.type', '');
      if (type === 'park-type') {
        targetParkId = this.hasDropdownOption(dropdown, targetParkId) ? targetParkId : defaultParkId;
      } else {
        targetPassId = this.hasDropdownOption(dropdown, targetPassId) ? targetPassId : defaultPassId;
      }
    });
    return {
      parkType: targetParkId === defaultParkId ? null : targetParkId,
      passType: targetPassId === defaultPassId ? null : targetPassId
    };
  }
  hasDropdownOption(dropdown, targetId) {
    if (!targetId) return false;
    const data = get(dropdown, 'data');
    const options = get(data, 'options', []);
    return options.some(opt => opt.value === targetId);
  }
  static {
    this.ɵfac = function GdsCalendarBlockoutDateService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarBlockoutDateService)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(WINDOW), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.PageLabelsResolver), i0.ɵɵinject(i5.HttpClient), i0.ɵɵinject(i6.ActivatedRoute), i0.ɵɵinject(i6.Router), i0.ɵɵinject(i7.SpinnerService), i0.ɵɵinject(i8.UprMobileService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarBlockoutDateService,
      factory: GdsCalendarBlockoutDateService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarBlockoutDateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4.PageLabelsResolver
  }, {
    type: i5.HttpClient
  }, {
    type: i6.ActivatedRoute
  }, {
    type: i6.Router
  }, {
    type: i7.SpinnerService
  }, {
    type: i8.UprMobileService
  }], null);
})();
class GdsCalendarCommonUtilityService {
  apiDateFormat(date, begin, addTime, zone) {
    const luxTime = isString(date) ? DateTime.fromISO(date, {
      zone
    }) : date;
    if (!luxTime.isValid) {
      throw new Error(`'${date}' is not a valid date`);
    }
    if (addTime) {
      return begin ? luxTime.toFormat('yyyy-MM-dd 00:00:01') : luxTime.toFormat('yyyy-MM-dd 23:59:59');
    }
    return luxTime.toFormat('yyyy-MM-dd');
  }
  dateFromFormat(dateString, format) {
    return DateTime.fromFormat(dateString, format);
  }
  fromISOToDate(isoString) {
    return DateTime.fromISO(isoString);
  }
  fromISOToFormat(isoString, format) {
    return DateTime.fromISO(isoString).toFormat(format);
  }
  fromDateToFormat(dateObject, format) {
    return dateObject.toFormat(format);
  }
  fromDateToISO(dateObject) {
    return dateObject.toISO();
  }
  addToDate(dateObject, duration) {
    return dateObject.plus(duration);
  }
  subtractFromDate(dateObject, duration) {
    return dateObject.minus(duration);
  }
  static {
    this.ɵfac = function GdsCalendarCommonUtilityService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarCommonUtilityService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarCommonUtilityService,
      factory: GdsCalendarCommonUtilityService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarCommonUtilityService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class GdsCalendarDiningService extends GdsCalendarService {
  constructor(endpoints, appDefaults, window, appEnvSrv, gdsCommonUtilities, localeService, httpService, spinner, pageLabelsService, notificationService) {
    super(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.endpoints = endpoints;
    this.appDefaults = appDefaults;
    this.window = window;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.httpService = httpService;
    this.spinner = spinner;
    this.pageLabelsService = pageLabelsService;
    this.notificationService = notificationService;
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_DINING,
      callback: this.getMonth,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_DINING,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  getDay({
    time = DateTime.now().setZone(this.timezone),
    zone = this.timezone,
    item
  }) {
    const day = super.getDay({
      time,
      zone
    });
    let availability, timeSlots;
    if (item?.availabilityResponse?.dates) {
      timeSlots = this.getDayTimeSlots(day.date, item.availabilityResponse.dates, zone);
      availability = timeSlots?.availability_status === 'AVAILABLE';
    } else {
      availability = timeSlots = false;
    }
    day.data = timeSlots ? timeSlots : {};
    day.available = availability;
    day.ariaLabel = `${DateTime.fromISO(day.date).setZone(zone).toFormat('LLL dd, yyyy')}`;
    return day;
  }
  // item is from this.endpoints.RESERVATION_AVAILABILITY
  // will not be defining item TYPE for now.
  getDayTimeSlots(date, item, zone = this.timezone) {
    // Need to match date format coming back from this endpoint
    // 2022-12-02T00:00:00
    let today = DateTime.fromISO(date).setZone(zone).toFormat('yyyy-MM-dd');
    today = today.concat('T00:00:00');
    return item.find(obj => {
      return obj.date === today;
    });
  }
  combineCalendarData(currentData, previousData) {
    const currentLastIndex = currentData.months.length - 1;
    const previousLastIndex = previousData.months.length - 1;
    const currentMonth = currentData.months[currentLastIndex].number;
    const currentYear = currentData.months[currentLastIndex].year;
    const prevMonth = previousData.months[previousLastIndex].number;
    const prevYear = previousData.months[previousLastIndex].year;
    const currentDate = DateTime.fromObject({
      month: currentMonth,
      year: currentYear
    });
    const prevDate = DateTime.fromObject({
      month: prevMonth,
      year: prevYear
    });
    if (currentDate > prevDate) {
      currentData = super.combineCalendarData(currentData, previousData);
    } else {
      currentData.months.push(...previousData.months);
      currentData.monthsByName = {
        ...currentData.monthsByName,
        ...previousData.monthsByName
      };
      currentData.monthsByNumber = {
        ...currentData.monthsByNumber,
        ...previousData.monthsByNumber
      };
    }
    currentData.startDate = previousData.startDate;
    return currentData;
  }
  getMonthPastPresentFuture(date) {
    const obj = super.getMonthPastPresentFuture(date, date, date);
    obj.isLast = false;
    return obj;
  }
  async getMonth({
    startDate,
    endDate,
    zone = this.timezone,
    item,
    previous,
    lastPossibleDate
  }) {
    const {
      luxStartTime,
      luxEndTime
    } = this.checkDates({
      startDate,
      endDate,
      zone
    });
    const lastMonthReached = !!(luxEndTime.toSeconds() >= lastPossibleDate.toSeconds());
    const place_id = item.availabilityRequest.place_id;
    const party_size = item.availabilityRequest.party_size;
    const show_reserved_times = item.availabilityRequest.show_reserved_times;
    const request = {
      start_date: luxStartTime.toFormat('yyyy-MM-dd'),
      end_date: luxEndTime.toFormat('yyyy-MM-dd'),
      party_size,
      show_reserved_times
    };
    const availability = from(this.httpService.httpRequest((this.endpoints.RESERVATION_AVAILABILITY || '').replace('{resort-area-code}', this.appDefaults.RESORT_AREA_CODE).replace('{place-id}', place_id), 'POST', request, !!item.availabilityRequest.headers ? item.availabilityRequest.headers : undefined));
    this.spinner.show('GDSCalendarGetDiningMonthRequest');
    await new Promise(resolve => {
      availability.subscribe({
        next: response => {
          item.availabilityResponse = response;
          if (item?.availabilityResponse?.dates.length < 1) {
            this.notificationService.show(this.pageLabels['com.upr.dining.max_guest_error_message'], 'error');
          }
          resolve(true);
          this.spinner.hide('GDSCalendarGetDiningMonthRequest');
        },
        error: () => {
          this.notificationService.show(this.pageLabels.ER71, 'error');
          resolve(true);
          this.spinner.hide('GDSCalendarGetDiningMonthRequest');
        }
      });
    });
    const monthData = await super.getMonth({
      startDate,
      endDate,
      zone,
      item,
      previous
    });
    const lastMonth = monthData.months[monthData.months.length - 1];
    lastMonth.isLast = lastMonthReached;
    return monthData;
  }
  getCalendarData({
    months,
    zone,
    item,
    previous
  }) {
    const data = super.getCalendarData({
      months,
      zone,
      item,
      previous
    });
    return data;
  }
  static {
    this.ɵfac = function GdsCalendarDiningService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarDiningService)(i0.ɵɵinject('RelativeUrls'), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(WINDOW), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4$1.DalService), i0.ɵɵinject(i7.SpinnerService), i0.ɵɵinject(i4.PageLabelsResolver), i0.ɵɵinject(i7$1.NotificationService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarDiningService,
      factory: GdsCalendarDiningService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarDiningService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['RelativeUrls']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4$1.DalService
  }, {
    type: i7.SpinnerService
  }, {
    type: i4.PageLabelsResolver
  }, {
    type: i7$1.NotificationService
  }], null);
})();
class GdsCalendarEventDayService extends GdsCalendarService {
  constructor(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService) {
    super(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.window = window;
    this.appDefaults = appDefaults;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_EVENTS,
      callback: this.getMonth,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_EVENTS,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  /**
   * It returns the calendar day for each month, exactly how hhn-events needed.
   * @param {IGetDayPropsEvents} props - IGetDayPropsEvents - props for the month.
   * @returns {ICalendarDayEvents} - ICalendarDayEvents - the calendar day for each month.
   * @memberof GdsCalendarEventDayService
   */
  getDay({
    time = DateTime.now(),
    zone = this.timezone
  }) {
    this.setZoneAndLocale(zone);
    const day = super.getDay({
      time,
      zone
    });
    const dayInaMonth = DateTime.fromISO(day?.date);
    const hasEvent = this.findEvent(dayInaMonth, zone);
    day.shortDate = zone.includes('Asia/Tokyo') ? day.shortDate : dayInaMonth.toFormat('LLLL d, yyyy');
    day.data = !!hasEvent ? hasEvent?.blockData : {};
    day.available = !!hasEvent && ['style-1', 'style-2'].includes(hasEvent.style);
    day.style = !!hasEvent && ['style-1', 'style-2'].includes(hasEvent.style) ? hasEvent.style : 'disabled';
    day.ariaLabel = day.longDate;
    day.img = this.setImageUrl(day);
    return day;
  }
  /**
   * Set the default time zone and locale.
   * @param {string} zone - The time zone to use.
   */
  setZoneAndLocale(zone) {
    Settings.defaultZone = zone;
    Settings.defaultLocale = this.locale;
  }
  /**
   * It returns the first event that has a date that matches the date.
   * @param {DateTime} dayInaMonth - DateTime - day in the month that we're checking for an event.
   * @memberof GdsCalendarEventDayService
   * @returns the event that is being found.
   */
  findEvent(dayInaMonth, zone) {
    this.setZoneAndLocale(zone);
    const calData = get(this.calendarConfig, 'calendarData', []);
    if (!Array.isArray(calData)) {
      return {};
    }
    return calData?.find(events => events?.eventDates?.some(date => this.isSameEventDay(dayInaMonth, date)));
  }
  /**
   * It returns true if the dayInaMonth and eventPlanned dates match.
   * @param {DateTime} dayInaMonth - DateTime - this is the day in the month
   * @param {string} date - The date of the event
   * @memberof GdsCalendarEventDayService
   * @returns a boolean value.
   */
  isSameEventDay(dayInaMonth, date) {
    const eventPlanned = DateTime.fromISO(date);
    return dayInaMonth.hasSame(eventPlanned, 'day') && dayInaMonth.hasSame(eventPlanned, 'month') && dayInaMonth.hasSame(eventPlanned, 'year');
  }
  /**
   * It returns the image url from the legendStyle array of objects, if it exists.
   * @param {ICalendarDayEvents} day - ICalendarDayEvents - day object that is passed in.
   * @memberof GdsCalendarEventDayService
   * @returns The image url for the legend style.
   */
  setImageUrl(day) {
    const styles = this.calendarConfig?.legendStyle;
    if (!Array.isArray(styles)) {
      return '';
    }
    const legend = styles.find(style => style.style === day.style);
    return legend?.image || '';
  }
  /**
   * It returns the calendar data for the month.
   * @param {IGetMonthEventProps} props - IGetMonthEventProps - props for the month.
   * @returns {MonthOutputTypeEvents} - MonthOutputTypeEvents - the calendar data for the month.
   * @memberof GdsCalendarEventDayService
   * @async
   * @returns {Promise<MonthOutputTypeEvents>}
   */
  async getMonth({
    startDate = DateTime.now().setZone(this.timezone),
    endDate,
    zone = this.timezone,
    item,
    previous,
    monthCount
  }) {
    this.calendarConfig = this.adjustEvents(item);
    const {
      luxStartTime,
      luxEndTime
    } = this.checkDates({
      startDate,
      endDate,
      zone,
      monthCount
    });
    return await super.getMonth({
      startDate: luxStartTime,
      endDate: luxEndTime,
      zone,
      item,
      previous
    });
  }
  /**
   * It returns the calendar month data in a custom format.
   * @param {CalendarDataEvents} calendarMonths - IGetCalendarDataPropsEvents - data that is passed in.
   * @returns months in a new object.
   * @memberof GdsCalendarEventDayService
   */
  monthManipulation(calendarMonths, zone) {
    this.setZoneAndLocale(zone);
    const oldMonths = get(calendarMonths, 'months', []);
    return oldMonths?.map(eachMonth => {
      const daysInMonth = [];
      const daysWithNull = get(eachMonth, 'daysByWeeks', [])?.flat(Infinity);
      daysWithNull?.forEach(day => {
        !day ? daysInMonth.push({
          name: 'adjacent'
        }) : daysInMonth.push(day);
      });
      const startDate = DateTime.fromISO(this.calendarConfig?.start_date);
      const endDate = DateTime.fromISO(this.calendarConfig?.end_date);
      return {
        heading: eachMonth?.longDate,
        month: eachMonth?.number,
        monthName: eachMonth?.name,
        year: eachMonth?.year,
        weekDays: this.shortWeekdays,
        daysInMonth: daysInMonth,
        nextLabel: this.nextLabel,
        prevLabel: this.prevLabel,
        isEventStart: startDate.month === eachMonth?.number && startDate.year === eachMonth?.year,
        isEventEnd: endDate.month === eachMonth?.number && endDate.year === eachMonth?.year,
        isCurrent: eachMonth?.isCurrent,
        isFuture: eachMonth?.isFuture,
        isLast: eachMonth?.isLast,
        isPast: eachMonth?.isPast,
        numberStyle: 'label-13'
      };
    });
  }
  getCalendarData({
    months,
    zone,
    item,
    previous
  }) {
    const data = super.getCalendarData({
      months,
      zone,
      item,
      previous
    });
    data.months = this.monthManipulation(data, zone);
    return data;
  }
  /**
   * It takes the data from the hhn calendar component and adjusts it.
   * @param {GdsEventDayCalendarConfig} data - The data that is being sent from gds-loader.
   * @returns with the calendarData and legendStyle properties adjusted.
   */
  adjustEvents(data) {
    const calendarData = get(data, 'calendarData.calendarConfig');
    unset(data, 'calendarData.calendarConfig');
    const legendStyles = get(data, 'legendStyle.styles');
    unset(data, 'legendStyle.styles');
    unset(data, 'resources');
    set(data, 'legendStyle', legendStyles);
    const eventsData = calendarData?.map(item => {
      const eventDates = item?.eventDates?.map(date => {
        return DateTime.fromFormat(date?.split(' ')[0], 'M/d/yyyy').toISO();
      });
      item.eventDates = eventDates;
      return item;
    });
    set(data, 'calendarData', eventsData);
    return data;
  }
  static {
    this.ɵfac = function GdsCalendarEventDayService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarEventDayService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.PageLabelsResolver));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarEventDayService,
      factory: GdsCalendarEventDayService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarEventDayService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4.PageLabelsResolver
  }], null);
})();
class GdsCalendarParkHoursService extends GdsCalendarService {
  constructor(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService, uprMobileService, poiService) {
    super(window, appDefaults, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.window = window;
    this.appDefaults = appDefaults;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
    this.uprMobileService = uprMobileService;
    this.poiService = poiService;
    this.endpointDateFormat = 'MM/dd/yyyy';
    this.hoursDateFormat = 'yyyy-MM-dd';
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_PARK_HOURS,
      callback: this.getMonthParkHours,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_PARK_HOURS,
      outputCallback: this.getCalendarData
    }, {
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_SHOW_TIMES,
      callback: this.getMonthShowTimes,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_SHOW_TIMES,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  getDay({
    time = DateTime.now(),
    zone = this.timezone,
    item = {}
  }) {
    const day = super.getDay({
      time,
      zone
    });
    this.dayManipulation(day, {
      time,
      zone,
      item
    });
    return day;
  }
  async getMonthParkHours({
    startDate,
    endDate,
    zone = this.timezone,
    item = {},
    previous
  }) {
    if (!startDate) {
      startDate = DateTime.now().setZone(zone);
    }
    const data = await this.getMonthParkHoursData({
      startDate,
      endDate,
      zone,
      item
    });
    const months = await this.getMonth({
      startDate: data?.firstAvailableDate?.toISO(),
      endDate: data?.lastAvailableDate?.endOf('month')?.toISO(),
      zone,
      item: data,
      previous,
      setCurrentToStartDate: true
    });
    return months;
  }
  async getMonthShowTimes({
    startDate,
    endDate,
    zone = this.timezone,
    item = {},
    previous
  }) {
    if (!startDate) {
      startDate = DateTime.now().setZone(zone);
    }
    const months = await this.getMonth({
      startDate,
      endDate,
      zone,
      item,
      previous
    });
    return months;
  }
  // Call this function before sending back
  getCalendarData({
    months,
    zone,
    item
  }) {
    const data = super.getCalendarData({
      months,
      zone,
      item
    });
    return data;
  }
  dayManipulation(day, {
    zone = this.timezone,
    item = {}
  }) {
    const currDate = DateTime.fromISO(day.date).setZone(zone).setLocale(this.getLocale());
    if (day.isPast || item.firstAvailableDate && item.firstAvailableDate > currDate) {
      return;
    }
    const longDateFormat = currDate.toLocaleString(DateTime.DATE_FULL);
    const venueHours = get(item, 'data.venueHours', {});
    const blockDataContainer = get(item, 'data.blockDataContainer', null);
    const blocksData = get(blockDataContainer, 'blocksData', []);
    const updatedBlocksData = blocksData.reduce((updatedBlocksData, block) => {
      const hoursData = get(venueHours, [block.venueID, currDate.toFormat(this.hoursDateFormat)], null);
      let venue;
      if (hoursData) {
        const dataString = this.poiService.replaceVenueHoursHashCodes(JSON.stringify(block), hoursData, zone);
        venue = JSON.parse(dataString);
      } else {
        venue = {
          ...block
        };
        const venueHoursById = get(venueHours, [block.venueID], {});
        const labelKey = venueHoursById === false ? 'txtDataError' : 'txtNoParkHours';
        venue.heading = get(this.pageLabels, labelKey, '');
        delete venue?.description;
        delete venue?.ctaButton;
      }
      updatedBlocksData.push(venue);
      return updatedBlocksData;
    }, []);
    // Keep date disabled if there are no park hours present.
    if (updatedBlocksData.length === 0) {
      day.available = false;
      return;
    }
    const heading = this.gdsCommonUtilities.replaceHashCodes(get(blockDataContainer, 'heading', ''), {
      longdateformat: longDateFormat
    });
    day.data = {
      ...blockDataContainer,
      heading,
      blocksData: updatedBlocksData
    };
    day.available = true;
    day.ariaLabel = currDate.toLocaleString(DateTime.DATE_HUGE);
  }
  async getMonthParkHoursData({
    startDate,
    endDate,
    zone = this.timezone,
    item = {}
  }) {
    const startDateObject = typeof startDate === 'string' ? DateTime.fromISO(startDate).setZone(zone) : startDate;
    const endDateObject = typeof endDate === 'string' ? DateTime.fromISO(endDate).setZone(zone) : endDate;
    const endDateFormatted = endDateObject?.toFormat(this.endpointDateFormat);
    const requests = [];
    const blockDataContainer = get(item, 'calendarBlockContainer', {});
    const blocks = get(blockDataContainer, 'blocksData', []);
    blocks.forEach(block => {
      const venueId = get(block, 'venueID', '');
      if (venueId) {
        requests.push(this.uprMobileService.fetchVenueHoursById(venueId, endDateFormatted).pipe(map(hours => ({
          id: venueId,
          hours
        })), catchError(() => of({
          id: venueId,
          hours: false
        }))));
      }
    });
    let lastAvailableDate = DateTime.now().endOf('month').setZone(zone);
    let firstAvailableDate = DateTime.now().startOf('day').setZone(zone);
    const venueHours = await firstValueFrom(zip(requests).pipe(map(responses => {
      return responses.reduce((venueHours, venue) => {
        const hours = get(venue, 'hours', []);
        if (!hours) {
          venueHours[venue.id] = false;
        } else {
          venueHours[venue.id] = hours.reduce((hoursByDate, day) => {
            hoursByDate[day.Date] = day;
            const currDateTime = DateTime.fromFormat(day.Date, this.hoursDateFormat).setZone(zone);
            if (currDateTime.isValid && (!endDateObject || currDateTime < endDateObject || this.isSameDay(endDateObject, currDateTime)) && currDateTime > lastAvailableDate) {
              lastAvailableDate = currDateTime;
            }
            if (currDateTime.isValid && (!startDateObject || currDateTime > startDateObject || this.isSameDay(startDateObject, currDateTime)) && currDateTime < firstAvailableDate) {
              firstAvailableDate = currDateTime;
            }
            return hoursByDate;
          }, {});
        }
        return venueHours;
      }, {});
    })));
    return {
      data: {
        blockDataContainer,
        venueHours
      },
      firstAvailableDate,
      lastAvailableDate,
      resources: item.resources
    };
  }
  static {
    this.ɵfac = function GdsCalendarParkHoursService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarParkHoursService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.PageLabelsResolver), i0.ɵɵinject(i8.UprMobileService), i0.ɵɵinject(i6$1.GdsPoiService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarParkHoursService,
      factory: GdsCalendarParkHoursService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarParkHoursService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4.PageLabelsResolver
  }, {
    type: i8.UprMobileService
  }, {
    type: i6$1.GdsPoiService
  }], null);
})();
class GdsCalendarStoreService extends GdsCalendarService {
  constructor(endpoints, defaultValues, window, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService, localStorage, commonUtility, cartService, notification, spinner) {
    super(window, defaultValues, appEnvSrv, gdsCommonUtilities, localeService, pageLabelsService);
    this.endpoints = endpoints;
    this.defaultValues = defaultValues;
    this.window = window;
    this.appEnvSrv = appEnvSrv;
    this.gdsCommonUtilities = gdsCommonUtilities;
    this.localeService = localeService;
    this.pageLabelsService = pageLabelsService;
    this.localStorage = localStorage;
    this.commonUtility = commonUtility;
    this.cartService = cartService;
    this.notification = notification;
    this.spinner = spinner;
    this.defaultCurrency = get(this.defaultValues, 'CURRENCY_SIGN');
  }
  init() {
    const events = [{
      name: CALENDAR_TRIGGER_EVENT_KEYS.GET_MONTH_STORE,
      callback: this.getMonth,
      outputName: CALENDAR_OUTPUT_EVENT_KEYS.OUT_GET_MONTH_STORE,
      outputCallback: this.getCalendarData
    }];
    this.registerEvents(events);
  }
  getDay({
    time = DateTime.now().setZone(this.timezone),
    zone = this.timezone,
    item
  }) {
    const day = super.getDay({
      time,
      zone
    });
    const productItem = this.getItemFromProducts(day.date, item);
    const ariaLabelPrefix = item?.products[0]?.ariaLabelPrefix;
    // Retrieve the quantity selected by the guest
    const quantity = Number(get(item, 'product.numberOfGuest.numberOfGuestAdult', 0)) + Number(get(item, 'product.numberOfGuest.numberOfGuestChild', 0));
    day.available = productItem.length ? this.isAvailable(productItem[0], quantity) : false;
    day.data = productItem;
    day.price = this.getPrice(day, item);
    day.ariaLabel = `${ariaLabelPrefix} - ${day.shortDate || DateTime.fromISO(day.date).toFormat('LLL dd, yyyy')} - ${this.getPrice(day, item)}`; // Needs ariaLabelPrefix
    return day;
  }
  getPrice(day, item) {
    const notAvailable = this.pageLabels.NotAvailableLabel;
    const apiFormattedDate = this.commonUtility.apiDateFormat(day.date, false, false, this.timezone);
    const childOnly = item?.products[0]?.isChildOnly;
    const prodIndex = childOnly ? 1 : 0;
    const productPrice = get(item, `products[${prodIndex}].availability[${apiFormattedDate}].pricing[0].amount`);
    // to support promo products introduced new attribute PERDAYCALC
    const numberOfDays = get(item, `product.perDayCalc`) || get(item, `products[${prodIndex}].days`);
    if (!day.available || !productPrice || !numberOfDays) {
      return notAvailable;
    }
    const price = !item?.product?.showTicketPrice ? productPrice / numberOfDays : productPrice;
    return `${this.defaultCurrency}${Math.ceil(price)}`;
  }
  isAvailable(item, quantity) {
    let available = get(item, 'inventoryEvents[0].available');
    let availableUnits = get(item, 'inventoryEvents[0].availableUnits');
    if (!available || !availableUnits) return false;
    available = parseInt(available, 10);
    availableUnits = parseInt(availableUnits, 10);
    return availableUnits > 0 && quantity <= availableUnits;
  }
  getItemFromProducts(date, item) {
    const apiFormattedDate = this.commonUtility.apiDateFormat(date, false, false, this.timezone);
    const products = item?.products;
    const productArray = [];
    products.forEach(product => {
      const availability = get(product, `availability[${apiFormattedDate}]`);
      if (availability) productArray.push(availability);
    });
    return productArray;
  }
  async getMonth({
    startDate = DateTime.now().setZone(this.timezone),
    endDate,
    zone = this.timezone,
    item,
    previous,
    monthCount
  }) {
    const {
      luxStartTime,
      luxEndTime
    } = this.checkDates({
      startDate,
      endDate,
      zone,
      monthCount
    });
    const apiFormatStart = this.commonUtility.apiDateFormat(luxStartTime, true, true, this.timezone);
    const apiFormatEnd = this.commonUtility.apiDateFormat(luxEndTime, false, true, this.timezone);
    const data = {
      contractId: this.localStorage.getItem('CONTRACT_ID'),
      currency: 'USD',
      events: map$1(item.products, currentPartNumber => {
        const event = {
          partNumber: currentPartNumber.partNumber,
          startDate: apiFormatStart,
          endDate: apiFormatEnd,
          quantity: currentPartNumber.quantity
        };
        return event;
      })
    };
    await this.cartService.httpRequest(this.endpoints.PRICING_INVENTORY, 'POST', data).then(response => {
      const eventAvailability = get(response, 'eventAvailability');
      item.inventoryResponse = eventAvailability;
      item.products.forEach(product => {
        const part = get(eventAvailability, product.partNumber);
        if (part) {
          product['availability'] = part;
        }
      });
    }).catch(error => {
      if (error.status !== 401) {
        this.notification.show(get(this.pageLabels, 'ER71'));
      }
      this.spinner.hide(SPINNER_KEYS.OVERRIDE);
      throw error;
    });
    return await super.getMonth({
      startDate,
      endDate: luxEndTime,
      zone,
      item,
      previous
    });
  }
  getCalendarData({
    months,
    zone,
    item,
    previous
  }) {
    const data = super.getCalendarData({
      months,
      zone,
      item,
      previous
    });
    return data;
  }
  static {
    this.ɵfac = function GdsCalendarStoreService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarStoreService)(i0.ɵɵinject('RelativeUrls'), i0.ɵɵinject('DefaultValues'), i0.ɵɵinject(WINDOW), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.GdsCommonUtilityService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.PageLabelsResolver), i0.ɵɵinject(i5$1.LocalStorageService), i0.ɵɵinject(GdsCalendarCommonUtilityService), i0.ɵɵinject(i7$2.CartService), i0.ɵɵinject(i7$1.NotificationService), i0.ɵɵinject(i7.SpinnerService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsCalendarStoreService,
      factory: GdsCalendarStoreService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarStoreService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['RelativeUrls']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.GdsCommonUtilityService
  }, {
    type: i3.LocaleService
  }, {
    type: i4.PageLabelsResolver
  }, {
    type: i5$1.LocalStorageService
  }, {
    type: GdsCalendarCommonUtilityService
  }, {
    type: i7$2.CartService
  }, {
    type: i7$1.NotificationService
  }, {
    type: i7.SpinnerService
  }], null);
})();
class GdsCalendarModule {
  constructor(gdsCalendar, gdsCalendarStore, gdsCalendarBlockoutDate, gdsCalendarParkHours, gdsCalendarDiningService, gdsCalendarEventDayService) {
    this.gdsCalendar = gdsCalendar;
    this.gdsCalendarStore = gdsCalendarStore;
    this.gdsCalendarBlockoutDate = gdsCalendarBlockoutDate;
    this.gdsCalendarParkHours = gdsCalendarParkHours;
    this.gdsCalendarDiningService = gdsCalendarDiningService;
    this.gdsCalendarEventDayService = gdsCalendarEventDayService;
  }
  static {
    this.ɵfac = function GdsCalendarModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsCalendarModule)(i0.ɵɵinject(GdsCalendarService), i0.ɵɵinject(GdsCalendarStoreService), i0.ɵɵinject(GdsCalendarBlockoutDateService), i0.ɵɵinject(GdsCalendarParkHoursService), i0.ɵɵinject(GdsCalendarDiningService), i0.ɵɵinject(GdsCalendarEventDayService));
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GdsCalendarModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [GdsCalendarService, GdsCalendarStoreService, GdsCalendarBlockoutDateService, GdsCalendarParkHoursService, GdsCalendarCommonUtilityService, GdsCalendarDiningService, GdsCalendarEventDayService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsCalendarModule, [{
    type: NgModule,
    args: [{
      providers: [GdsCalendarService, GdsCalendarStoreService, GdsCalendarBlockoutDateService, GdsCalendarParkHoursService, GdsCalendarCommonUtilityService, GdsCalendarDiningService, GdsCalendarEventDayService]
    }]
  }], () => [{
    type: GdsCalendarService
  }, {
    type: GdsCalendarStoreService
  }, {
    type: GdsCalendarBlockoutDateService
  }, {
    type: GdsCalendarParkHoursService
  }, {
    type: GdsCalendarDiningService
  }, {
    type: GdsCalendarEventDayService
  }], null);
})();

//---------------- REQUEST TYPES --------------------------
// export interface CalendarOptions {
// 	type?: 'select' | 'multiselect' | 'display' | 'daterange';
// 	multiStartDate?: string[];
// 	startDate?: string;
// 	endDate?: string | null;
// 	monthSelection?: boolean;
// 	pastDates?: boolean;
// 	showDatesFromOtherMonths?: boolean;
// 	shortMonthLabels?: boolean;
// 	changeToListView?: boolean;
// 	customDateFormat?: string;
// 	customHeaderFormat?: string;
// }

//---------------- CALENDAR TYPES --------------------------

//---------------- CALENDAR TYPES --------------------------

//---------------- CALENDAR TYPES --------------------------

//---------------- CALENDAR TYPES --------------------------
//---------------- REQUEST TYPES --------------------------

//---------------- CALENDAR TYPES --------------------------

/*
 * Public API Surface of gds-calendar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CALENDAR_OUTPUT_EVENTS_WITH_ERROR_HANDLING, CALENDAR_OUTPUT_EVENT_KEYS, CALENDAR_TRIGGER_EVENT_KEYS, GdsCalendarBlockoutDateService, GdsCalendarCommonUtilityService, GdsCalendarDiningService, GdsCalendarEventDayService, GdsCalendarModule, GdsCalendarParkHoursService, GdsCalendarService, GdsCalendarStoreService };
